import React from "react";
import { Box, MenuItem, TextField } from "@mui/material";

export const CARE_RESIDENT_GENDER_OPTIONS = ["", "Female", "Male"] as const;
export const CARE_RESIDENT_SITUATION_OPTIONS = [
  "Hospital",
  "Rehab",
  "Home",
  "Urgent discharge",
] as const;
export const CARE_RESIDENT_BUDGET_OPTIONS = [
  "$3,000 - $5,000",
  "$5,000 - $7,000",
  "$7,000+",
  "Not sure",
] as const;
export const CARE_RESIDENT_PRIVATE_ROOM_OPTIONS = ["", "Yes", "No"] as const;
export const CARE_RESIDENT_CAREGIVER_PREFERENCE_OPTIONS = [
  "",
  "No preference",
  "Male",
  "Female",
] as const;

export type CareResidentDetailsFieldValue = {
  residentAge: string;
  gender: string;
  currentSituation: string;
  currentLocationText: string;
  budget: string;
  privateRoomNeeded: string;
  caregiverPreference: string;
};

type CareResidentDetailsFieldsProps = {
  value: CareResidentDetailsFieldValue;
  onChange: (next: Partial<CareResidentDetailsFieldValue>) => void;
  showResidentAge?: boolean;
  showGender?: boolean;
  showCurrentSituation?: boolean;
  showCurrentLocationText?: boolean;
  showBudget?: boolean;
  showPrivateRoomNeeded?: boolean;
  showCaregiverPreference?: boolean;
  currentLocationLabel?: string;
  currentLocationHelperText?: string;
  budgetLabel?: string;
  budgetError?: boolean;
  budgetHelperText?: string;
};

export default function CareResidentDetailsFields({
  value,
  onChange,
  showResidentAge = true,
  showGender = true,
  showCurrentSituation = true,
  showCurrentLocationText = true,
  showBudget = true,
  showPrivateRoomNeeded = true,
  showCaregiverPreference = true,
  currentLocationLabel = "Current location / facility",
  currentLocationHelperText,
  budgetLabel = "Budget",
  budgetError = false,
  budgetHelperText,
}: CareResidentDetailsFieldsProps) {
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
        gap: 2,
      }}
    >
      {showResidentAge ? (
        <TextField
          size="small"
          label="Resident age"
          value={value.residentAge}
          onChange={(event) => onChange({ residentAge: event.target.value })}
        />
      ) : null}

      {showGender ? (
        <TextField
          size="small"
          select
          label="Gender"
          value={value.gender}
          onChange={(event) => onChange({ gender: event.target.value })}
        >
          <MenuItem value="">
            <em>Not specified</em>
          </MenuItem>
          {CARE_RESIDENT_GENDER_OPTIONS.filter(Boolean).map((option) => (
            <MenuItem key={option} value={option}>
              {option}
            </MenuItem>
          ))}
        </TextField>
      ) : null}

      {showCurrentLocationText ? (
        <TextField
          size="small"
          label={currentLocationLabel}
          value={value.currentLocationText}
          onChange={(event) => onChange({ currentLocationText: event.target.value })}
          helperText={currentLocationHelperText}
        />
      ) : null}

      {showCurrentSituation ? (
        <TextField
          size="small"
          select
          label="Current situation"
          value={value.currentSituation}
          onChange={(event) => onChange({ currentSituation: event.target.value })}
        >
          <MenuItem value="">
            <em>Not specified</em>
          </MenuItem>
          {CARE_RESIDENT_SITUATION_OPTIONS.map((option) => (
            <MenuItem key={option} value={option}>
              {option}
            </MenuItem>
          ))}
        </TextField>
      ) : null}

      {showBudget ? (
        <TextField
          size="small"
          select
          label={budgetLabel}
          value={value.budget}
          onChange={(event) => onChange({ budget: event.target.value })}
          error={budgetError}
          helperText={budgetHelperText}
        >
          <MenuItem value="">
            <em>Not specified</em>
          </MenuItem>
          {CARE_RESIDENT_BUDGET_OPTIONS.map((option) => (
            <MenuItem key={option} value={option}>
              {option}
            </MenuItem>
          ))}
        </TextField>
      ) : null}

      {showPrivateRoomNeeded ? (
        <TextField
          size="small"
          select
          label="Private room needed?"
          value={value.privateRoomNeeded}
          onChange={(event) => onChange({ privateRoomNeeded: event.target.value })}
        >
          <MenuItem value="">
            <em>Not specified</em>
          </MenuItem>
          {CARE_RESIDENT_PRIVATE_ROOM_OPTIONS.filter(Boolean).map((option) => (
            <MenuItem key={option} value={option}>
              {option}
            </MenuItem>
          ))}
        </TextField>
      ) : null}

      {showCaregiverPreference ? (
        <TextField
          size="small"
          select
          label="Male/Female caregiver preference"
          value={value.caregiverPreference}
          onChange={(event) => onChange({ caregiverPreference: event.target.value })}
        >
          <MenuItem value="">
            <em>Not specified</em>
          </MenuItem>
          {CARE_RESIDENT_CAREGIVER_PREFERENCE_OPTIONS.filter(Boolean).map((option) => (
            <MenuItem key={option} value={option}>
              {option}
            </MenuItem>
          ))}
        </TextField>
      ) : null}
    </Box>
  );
}
